/**
 * AK-423 (PR-E) — чистые GeoJSON-хелперы для мини-карты контура поля во вкладке
 * «Геометрия». Без MapLibre/DOM — юнит-тестируемы.
 *
 * Мини-карта рисует эталонный контур + зоны как локальные GeoJSON-слои (никакого
 * MVT — это карточка поля, не /map). Отсюда нужны две операции:
 *   - geoBounds  — общий bbox набора геометрий для fitBounds по контуру;
 *   - toFeatureCollection — обернуть геометрию поля/зоны в FeatureCollection
 *     с произвольными properties (цвет, id) для source карты.
 */
import type { FeatureCollection, Geometry } from 'geojson';

/** Мягкий тип входной геометрии (как приходит из /contours и /subzones). */
export interface MiniGeo {
  type?: string;
  coordinates?: unknown;
}

/** bbox в порядке MapLibre fitBounds: [[west, south], [east, north]]. */
export type Bounds = [[number, number], [number, number]];

/** Обходит каждую пару [lng, lat] у Polygon / MultiPolygon. Прочее — игнор. */
function eachCoord(
  geo: MiniGeo | null | undefined,
  fn: (lng: number, lat: number) => void,
): void {
  if (!geo || !geo.coordinates) return;
  let rings: unknown[] = [];
  if (geo.type === 'Polygon') {
    rings = geo.coordinates as unknown[];
  } else if (geo.type === 'MultiPolygon') {
    rings = (geo.coordinates as unknown[][]).flat();
  }
  for (const ring of rings) {
    if (!Array.isArray(ring)) continue;
    for (const pt of ring) {
      if (
        Array.isArray(pt) &&
        typeof pt[0] === 'number' &&
        typeof pt[1] === 'number'
      ) {
        fn(pt[0], pt[1]);
      }
    }
  }
}

/**
 * Общий bbox набора геометрий (для fitBounds). null, если ни одной валидной
 * координаты — карта тогда остаётся на дефолтном центре.
 */
export function geoBounds(geos: Array<MiniGeo | null | undefined>): Bounds | null {
  let w = Infinity;
  let s = Infinity;
  let e = -Infinity;
  let n = -Infinity;
  for (const g of geos) {
    eachCoord(g, (lng, lat) => {
      if (lng < w) w = lng;
      if (lng > e) e = lng;
      if (lat < s) s = lat;
      if (lat > n) n = lat;
    });
  }
  if (!isFinite(w) || !isFinite(s) || !isFinite(e) || !isFinite(n)) return null;
  return [
    [w, s],
    [e, n],
  ];
}

/** Пустая FeatureCollection (source-заглушка до появления геометрии). */
export function emptyFC(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

/**
 * Обернуть одну геометрию поля/зоны в FeatureCollection с properties (цвет, id).
 * Не-полигон / пусто → пустая коллекция (source остаётся валидным).
 */
export function toFeatureCollection(
  geo: MiniGeo | null | undefined,
  props: Record<string, unknown> = {},
): FeatureCollection {
  if (
    !geo ||
    !geo.coordinates ||
    (geo.type !== 'Polygon' && geo.type !== 'MultiPolygon')
  ) {
    return emptyFC();
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: props,
        geometry: geo as unknown as Geometry,
      },
    ],
  };
}

/**
 * Собрать FeatureCollection зон: по одной фиче на зону с properties.color/id.
 * Скрытые (в hidden) — исключаются; невалидные геометрии — пропускаются.
 */
export function zonesFeatureCollection(
  zones: Array<{ id: string; geojson: MiniGeo | null; color: string }>,
  hidden: Set<string>,
): FeatureCollection {
  const features = zones
    .filter((z) => !hidden.has(z.id) && z.geojson?.coordinates)
    .filter(
      (z) =>
        z.geojson?.type === 'Polygon' || z.geojson?.type === 'MultiPolygon',
    )
    .map((z) => ({
      type: 'Feature' as const,
      properties: { id: z.id, color: z.color },
      geometry: z.geojson as unknown as Geometry,
    }));
  return { type: 'FeatureCollection', features };
}
